// Pure, timezone-safe helpers for the analytics date-range selector.
//
// The Heavy Users report aggregates per-day Firestore buckets named
// `daily_YYYYMMDD`, where YYYYMMDD is a UTC calendar day. To make the Ops
// selector line up exactly with those buckets we:
//   1. build ranges on UTC day boundaries (so a local-midnight → UTC conversion
//      can never shift the calendar day), and
//   2. emit an inclusive [start, end] range that covers EXACTLY the requested
//      number of days (a "Last 7 days" preset must query 7 buckets, not 8).
//
// These functions are intentionally free of side effects and framework imports
// so they can be unit-tested directly.

export interface AnalyticsUtcRange {
  /** ISO timestamp at 00:00:00.000 UTC of the first day in range. */
  start: string;
  /** ISO timestamp at 23:59:59.999 UTC of the last day in range. */
  end: string;
}

/** Start-of-day (00:00:00.000) in UTC for the given date. */
export function startOfUtcDay(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0),
  );
}

/** End-of-day (23:59:59.999) in UTC for the given date. */
export function endOfUtcDay(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999),
  );
}

/**
 * Inclusive UTC range covering EXACTLY `days` calendar days, ending on the UTC
 * day of `now`. For days=7 this yields today plus the previous six days (seven
 * `daily_YYYYMMDD` buckets), not eight.
 */
export function presetUtcRange(days: number, now: Date = new Date()): AnalyticsUtcRange {
  const safeDays = Number.isFinite(days) && days > 0 ? Math.floor(days) : 7;
  const end = endOfUtcDay(now);
  const startDay = new Date(end);
  startDay.setUTCDate(startDay.getUTCDate() - (safeDays - 1));
  return { start: startOfUtcDay(startDay).toISOString(), end: end.toISOString() };
}

/**
 * Inclusive UTC range from two `YYYY-MM-DD` values (as produced by an
 * `<input type="date">`). The strings are interpreted as UTC calendar days, so
 * the resulting range never drifts by the viewer's timezone offset.
 */
export function customUtcRange(startYmd: string, endYmd: string): AnalyticsUtcRange {
  const start = startOfUtcDay(new Date(`${startYmd}T00:00:00.000Z`));
  const end = endOfUtcDay(new Date(`${endYmd}T00:00:00.000Z`));
  return { start: start.toISOString(), end: end.toISOString() };
}

/**
 * Convert an ISO timestamp (or `Date`) to a `YYYYMMDD` string using UTC, so it
 * matches the backend's `daily_YYYYMMDD` bucket ids exactly.
 */
export function toYyyymmddUtc(value?: string | Date): string | undefined {
  if (!value) return undefined;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

/** True when `value` is a well-formed `YYYY-MM-DD` calendar date. */
export function isValidYmd(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && toYyyymmddUtc(d) === value.replace(/-/g, '');
}
